<template>
  <q-dialog v-model="show" position="bottom">
    <q-card class="picker">
      <div class="picker-head">
        <div class="picker-head-tile">
          {{ $t(`checkout.${isDelivery ? 'timeDELIVERY' : 'timePICK_UP'}`) }}
        </div>
        <div class="picker-head-text">{{ displayLabel }}</div>
        <q-icon class="picker-head-close" name="svg:oclose" @click="onCancel" />
      </div>
      <VuePicker
        ref="picker"
        class="picker-body"
        :show-toolbar="true"
        :data="[leftCol, rightCol]"
        :row-number="7"
        :default-index="defaultIndex"
        @change="onChange"
      />
      <div class="picker-footer">
        <button class="picker-btn cancel" @click="onCancel">
          {{ $t('checkout.cancel') }}
        </button>
        <button class="picker-btn confirm" @click="onConfirm">
          {{ $t('checkout.save') }}
        </button>
      </div>
    </q-card>
  </q-dialog>
</template>

<script>
import { mapState } from 'vuex';
import add from 'date-fns/add';
import addMill from 'date-fns/addMilliseconds';
import set from 'date-fns/set';
import getTime from 'date-fns/getTime';
import compareAsc from 'date-fns/compareAsc';
import isWithinRange from 'date-fns/isWithinInterval';
import last from 'lodash/last';
import { getCoverPage } from 'src/api/home';
import { formatDate, isDef, useRetry } from 'src/utils/base';
import VuePicker from 'src/components/Picker/index.vue';
import data from './test_data.json';
import { CHECKOUT_OPTION_LOADED } from '../../../../utils/config';

const WEEK_MAP = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 0,
};
const STEP = 30 * 60 * 1000; // 步进间隔时间

export default {
  components: { VuePicker },
  props: {
    days: {
      type: Number,
      default: 7,
    },
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      show: false,
      businessHour: [],
      defaultIndex: [0, 0],
      picked: {
        left: null,
        right: null,
      },
      // 可选时间分组结果 { 123123131: [] }
      groupBy: new Map(),
    };
  },
  computed: {
    ...mapState({
      scheduled: (state) => state.prefetch.storeInfo?.scheduled ?? {},
    }),
    // 备餐时间
    PREPARING() {
      return this.scheduled.scheduledPrepareTime ?? 0;
    },
    // 缓存时间
    BUFFER() {
      return this.scheduled.scheduledBufferTime ?? 0;
    },
    // 配送时间
    DELIVERY() {
      return this.scheduled.scheduledDeliveryTime ?? 0;
    },
    displayLabel() {
      const { left, right } = this.picked || {};
      return `${left?.label} , ${right?.label}`;
    },
    isPickup() {
      return this.accessStatus === 'PICK_UP';
    },
    needAddTime() {
      const { BUFFER, DELIVERY, PREPARING } = this;
      return BUFFER + 300000 + PREPARING + (this.isDelivery ? DELIVERY : 0);
    },
    // 每周周几营业 [1, 2, 3, 4]
    businessDayForWeek() {
      if (this.businessHour.length === 0) return [];
      return this.businessHour.map((it) => WEEK_MAP[it.date]);
    },
    // 每周周几营业 { 1: {} 2: {},	3: {}, 4: {} }
    businessDayForWeekMap() {
      if (this.businessHour.length === 0) return {};
      return this.businessHour.reduce((map, it) => {
        map[WEEK_MAP[it.date]] = it;
        return map;
      }, {});
    },
    // 每天的开始时间和结束时间
    businessDayWithTimeStemp() {
      if (this.businessHour.length === 0) return {};
      return this.businessHour.reduce((pre, it, index) => {
        const { date, time = '', allDay } = it;
        let times = time?.split(',') || [];
        times = times.map((t) => {
          let [start, end] = t.split('-');
          const [startHour, startMin] = start.split(':');
          const [endHour, endMin] = end.split(':');
          start = { hours: startHour, minutes: startMin };
          end = { hours: endHour, minutes: endMin };
          return { start, end };
        });
        pre[WEEK_MAP[date]] = times;
        return pre;
      }, {});
    },
    // 获取所有营业时间的时间段
    allSteps() {
      return this.getAllSteps();
    },
    leftCol() {
      const days = [...this.groupBy.keys()] ?? [];
      return days.map((it) => ({
        label: formatDate(parseInt(it, 10), 'E d MMM', this.language),
        value: it,
      }));
    },
    rightCol() {
      const { groupBy, picked, isPickup } = this;
      const { left } = picked;
      const key = left?.value;
      if (!key) return [];
      const rightData = groupBy.get(key) ?? [];
      const week = new Date(+key).getDay();
      const lastWorkEnd = last(this.businessDayWithTimeStemp[week])?.end;
      const { hours, minutes } = lastWorkEnd || { hours: 0, minutes: 0 };
      return rightData.map((it, index) => {
        const isLast = index === rightData.length - 1;
        // 最后一个时间，pickup模式不能超过营业结束时间
        const rightEndDate =
          isLast && isPickup
            ? set(it, { hours, minutes })
            : add(it, { minutes: 30 });
        return {
          isLast,
          label: `${formatDate(it, 'HH:mm')}- ${formatDate(
            rightEndDate,
            'HH:mm',
          )}`,
          value: it,
        };
      });
    },
    leftAndRight() {
      return { left: this.leftCol, right: this.rightCol };
    },
    rangeEndTime() {
      const { picked } = this;
      const { right } = picked;
      const key = right?.value;
      if (!key) return 0;
      const week = new Date(+key).getDay();
      const lastWorkEnd = last(this.businessDayWithTimeStemp[week])?.end;
      const ret = set(right.value, {
        hours: lastWorkEnd.hours,
        minutes: lastWorkEnd.minutes,
      });
      return ret;
    },
  },
  watch: {
    show(value) {
      if (value && !this.value.left?.value && !this.value.right?.value) {
        this.computedColData();
      }
      if (value) {
        this.moveToValueIndex();
      }
    },
    leftAndRight(value) {
      const { left, right } = value;
      if (left && left.length && right && right.length) {
        this.moveToValueIndex();
      }
    },
  },
  mounted() {
    // 监听checkout option接口响应后，再发起storeHomePage接口，后端要求避免和checkoutOpton并发
    this.$bus.$once(CHECKOUT_OPTION_LOADED, this.fetchData);
  },
  methods: {
    fetchData() {
      useRetry(getCoverPage).then((res) => {
        if (res.code === 200) {
          this.businessHour = res.data?.businessHour || [];
          this.computedColData();
        }
      });
    },
    // 获取所有营业时间段
    getAllSteps() {
      const now = new Date();
      const asteps = [];
      for (let i = 0; i < this.days + 1; i += 1) {
        let innerNow = add(now, { days: i });
        innerNow = set(innerNow, { seconds: 0 });
        const week = innerNow.getDay();
        const { time } = this.businessDayForWeekMap[week] || { time: '' };
        // eslint-disable-next-line no-continue
        if (!this.businessDayForWeek.includes(week)) continue;
        const times = time.split(',');
        times.forEach((t) => {
          const [start, end] = t.split('-');
          const [startHour, startMin] = start.split(':');
          const [endHour, endMin] = end.split(':');
          const step = {
            start: set(innerNow, { hours: startHour, minutes: startMin }),
            end: set(innerNow, { hours: endHour, minutes: endMin }),
          };
          asteps.push(step);
        });
      }
      return asteps;
    },
    // 计算time数据
    computedColData() {
      /**
       * start: 要确定第一个开始计算的时间，即离现在最近的营业时间，如果当前就是营业时间，则取当前时间
       * baseTime: 确定第一个可选时间， start + needAddTime，结果在营业时间内 则使用这个时间，否则基于这个时间取下一个营业时间
       */
      const now = new Date();
      const { allSteps } = this;
      if (allSteps.length === 0) return;
      const lastStep = last(allSteps);
      let start = now;
      // 找到离当前时间最近的一个营业时间，营业时间内第一个比当前时间大的时间就是下一个营业时间
      const nextWorkTime = allSteps.find(
        (range) => compareAsc(range.start, now) > 0,
      );
      // 当前时间是否在营业时间内
      const nowInWork = allSteps.find((range) => isWithinRange(now, range));
      // 不在营业时间段呢，则使用最近营业时间的开始时间
      if (!nowInWork) {
        start = nextWorkTime.start;
      }
      // 计算出第一个可选时间 baseTime
      let baseTime = addMill(start, this.needAddTime);
      // 这里同样判断可选时间在不在营业时间内，在 ? 使用这个时间 : 使用下一个营业开始时间
      const inWork = allSteps.find((range) => isWithinRange(baseTime, range));
      // 第一个可选时间不在营业时间段内
      if (!inWork) {
        // 找到离计算出的第一个可选时间最近的一个营业时间
        const nextWorkTimeByAdded = allSteps.find(
          (range) => compareAsc(range.start, baseTime) > 0,
        );
        // 第一个可选择时间需要设置为：下一个可选时间的开始时间
        baseTime = nextWorkTimeByAdded.start;
      }
      // 从所有时间段内筛选出可选择的时间段
      const selectableInterval = allSteps.filter((range) => {
        // 第一个条件：baseTime 在时间段那范围内(保证了如果在时间段内的这个时间段)
        const inRange = isWithinRange(baseTime, range);
        // 第二个条件：时间段的开始时间 >= baseTime (筛选出在 baseTime 之后的所有时间段)
        const overUp = compareAsc(range.start, baseTime) >= 0;
        return inRange || overUp;
      });
      // 根据可选时间段和 baseTime 循环出所有的可选时间
      const selectableTimes = selectableInterval.reduce(
        (times, range, index) => {
          // 这里如果不是第一个时间段，时间需要设定为这个时间段的开始时间，如果是第一个时间段，则使用baseTime
          if (index !== 0) baseTime = range.start;
          // 将 “秒、毫秒设定为0” 以防止在最后一个时间的时候导致不可用
          baseTime = set(baseTime, { seconds: 0, milliseconds: 0 });
          // 这里的range.end时间段时没有秒和毫秒的，所以需要清除掉，这样避免了baseTime刚好等于range.end时间段的时候，导致不可用
          while (compareAsc(baseTime, range.end) <= 0) {
            times.push(baseTime);
            baseTime = addMill(baseTime, STEP);
          }
          return times;
        },
        [],
      );
      // 将所有可选时间按照天的日期分组{ '1652173908899': [可选时间] }
      const groupBy = selectableTimes.reduce((group, time) => {
        // key需要抹掉以下时间
        const key = set(time, {
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        }).getTime();
        if (!group.has(key)) {
          group.set(key, [time]);
        } else {
          const value = group.get(key);
          group.set(key, [...value, time]);
        }
        return group;
      }, new Map());
      this.groupBy = groupBy;
    },
    // =============LIEE============
    onChange(info, index) {
      const col1 = info[0];
      const col2 = info[1];
      if (col1) Object.assign(this.picked, { left: col1 });
      if (col2) Object.assign(this.picked, { right: col2 });
    },
    onConfirm() {
      // eslint-disable-next-line object-curly-newline
      const { displayLabel: label, picked, rangeEndTime, isPickup } = this;
      const { left, right } = picked;
      const timestemp = right.value?.getTime() || 0;
      const week = right.value?.getDay() || 1;
      const isLast = right?.isLast;
      this.$emit('confirm', {
        label,
        timestemp,
        ...picked,
        week,
        rangeEndTime:
          isLast && isPickup ? rangeEndTime.getTime() : timestemp + 1800000,
      });
      this.show = false;
    },
    onCancel() {
      this.$emit('cancel', this.picked);
      this.show = false;
    },
    // 计算总时间
    computedTotalTime(time, flag) {
      // eslint-disable-next-line object-curly-newline
      const { BUFFER, DELIVERY, PREPARING } = this;
      return (
        time + (BUFFER + 300000) + PREPARING + (this.isDelivery ? DELIVERY : 0)
      );
    },
    // 移动到给定时间索引
    moveToValueIndex() {
      const { leftCol, rightCol, value } = this;
      if (!value) return;
      const { left, right } = value;
      const leftIndex = leftCol.findIndex((item) => item.value === left.value);
      const rightIndex = rightCol.findIndex(
        (item) => compareAsc(item.value, new Date(right.value)) === 0,
      );
      this.defaultIndex = [Math.max(0, leftIndex), Math.max(0, rightIndex)];
    },
    // 下单时间校验 Deprecated
    onCheckOrderTime() {
      // eslint-disable-next-line object-curly-newline
      const { value, allSteps, PREPARING, BUFFER, DELIVERY, isDelivery } = this;
      if (!value?.timestemp || !isDef(value?.week)) return;
      const { week } = value;
      const now = new Date();
      const timestemp = new Date(value?.timestemp || 0);
      // 判断所选的时间是否在营业时间内
      const inBusiness = allSteps.some((step) => {
        let { start, end } = step;
        start = set(start, { milliseconds: 0 });
        end = set(end, { milliseconds: 0 });
        return isWithinRange(timestemp, { start, end });
      });
      // 判断选择的时间是否大于allTime（实际计算时间）
      const allTime =
        now.getTime() + PREPARING + BUFFER + (isDelivery ? DELIVERY : 0);
      const orderValid = allTime < timestemp;
      return inBusiness && orderValid;
    },
  },
};
</script>
<style scoped lang="scss">
.picker {
  padding: 22px 0;
  &-head {
    position: relative;
    padding: 0 16px 20px;
    &-tile {
      font-size: 12px;
    }
    &-text {
      color: #1f2937;
      font-size: 14px;
      font-weight: bold;
    }
    &-close {
      position: absolute;
      right: 16px;
      top: 16px;
      transform: translateY(-50%);
    }
  }
  &-body {
    background: rgba(249, 250, 251);
  }
  &-footer {
    display: flex;
    padding: 14px 16px 0;
  }
  &-btn {
    & + .picker-btn {
      margin-left: 16px;
    }
    width: 100%;
    height: 50px;
    line-height: 50px;
    font-weight: bold;
    color: #fff;
    background: $primary;
    border-radius: 8px;
    outline: none;
    border: none;
    font-size: 14px;
    &.cancel {
      background: #fff;
      color: #374151;
      border: 1px solid #d1d5db;
    }
  }
}
</style>
